import * as React from "react";
import { useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
  STATE_LABEL,
  STATE_COLOR,
  toDateInput,
  toInclusiveInput,
  toStoredThrough,
} from "../utils/eligibilityDisplay";
import { editEligibilityPeriod } from "../services/eligibilityApi";
import EligibilityImpactAck from "./EligibilityImpactAck";

// Edit the authoritative dates of an already-CONFIRMED period (open or closed). This is NOT a
// re-confirmation: the server preserves reviewed_by/reviewed_at and records the editor via
// updated_by/updated_at. Start/End editable; opening an interval (removing the End) or narrowing it
// (moving/adding the End earlier) reuses the same server-side Tier-1 impact check + Option-B
// acknowledgment as Confirm — evaluated against the resulting union of the franchise's active
// confirmed periods with this row's edited dates substituted.
export default function EligibilityEditDialog({ open, period, comCode, franchiseName, onClose, onEdited }) {
  const originalStart = period ? toDateInput(period.enabled_from_date) : "";
  const originalEnd = period ? toInclusiveInput(period.enabled_through_date) : "";

  const [start, setStart] = useState(originalStart);
  const [end, setEnd] = useState(originalEnd);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [impact, setImpact] = useState(null);
  const [acknowledged, setAcknowledged] = useState(false);

  if (!period) return null;

  const changedDates = start !== originalStart || end !== originalEnd;
  const needsAck = impact !== null && impact.count > 0;

  const changeStart = (v) => { setStart(v); setImpact(null); setAcknowledged(false); };
  const changeEnd = (v) => { setEnd(v); setImpact(null); setAcknowledged(false); };

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleSave = async () => {
    setError(null);
    if (!start) { setError("Start Date is required."); return; }
    if (end && end < start) { setError("End Date must be on or after Start Date."); return; }

    setSubmitting(true);
    try {
      const result = await editEligibilityPeriod(period.enablement_period_id, {
        enabledFromDate: start,
        enabledThroughDate: toStoredThrough(end),
        acknowledged,
      });
      if (result.requiresAcknowledgment) {
        setImpact(result.impact);
        setSubmitting(false);
        return;
      }
      onEdited(`Eligibility updated for ${comCode}`);
    } catch (e) {
      setError(e.message || "Save failed.");
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit eligibility period</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Typography variant="body2">
            <strong>{comCode}</strong>{franchiseName ? ` · ${franchiseName}` : ""}
          </Typography>
          <Chip
            size="small"
            label={STATE_LABEL[period.eligibility_state] ?? period.eligibility_state}
            color={STATE_COLOR[period.eligibility_state] ?? "default"}
          />
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Change the authoritative dates. This stays <strong>confirmed</strong> and records you as the
          editor; the original reviewer is preserved. Leave the End date empty to keep it open-ended.
        </Typography>

        <Box sx={{ display: "flex", gap: 2, mb: 1.5 }}>
          <TextField
            type="date" label="Start Date" required value={start}
            onChange={(e) => changeStart(e.target.value)}
            InputLabelProps={{ shrink: true }} size="small" fullWidth
          />
          <TextField
            type="date" label="End Date (inclusive; empty = open-ended)" value={end}
            onChange={(e) => changeEnd(e.target.value)}
            InputLabelProps={{ shrink: true }} size="small" fullWidth
          />
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 2, rowGap: 0.5, mb: 1.5 }}>
          <Typography variant="body2" color="text.secondary">Confirmed by</Typography>
          <Typography variant="body2">{period.reviewed_by || "—"}</Typography>
          <Typography variant="body2" color="text.secondary">Evidence / reason</Typography>
          <Typography variant="body2">{period.evidence_note || "—"}</Typography>
          <Typography variant="body2" color="text.secondary">Source</Typography>
          <Typography variant="body2">{period.source || "—"}</Typography>
        </Box>

        <EligibilityImpactAck impact={impact} acknowledged={acknowledged} onChange={setAcknowledged} />

        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={submitting || !changedDates || (needsAck && !acknowledged)}
        >
          {submitting ? "Saving…" : "Save changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
